import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ASSISTANT_STOPPED,
  ECO_CLIENT_SUGGESTIONS,
  ECO_JOB_SUGGESTIONS,
  ECO_SUGGESTIONS,
  type AssistantFocus,
  type AssistantRunRequest,
} from '@fieldops/shared'
import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { cn } from '../../lib/cn.js'
import { useSession } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'
import { Icon, type IconName } from '../Icon.js'
import { useConversation, type PhoneText } from '../job-card/assistant/useConversation.js'
import { SmsCompose } from '../job-card/SmsCompose.js'
import { Spinner } from '../Loader.js'
import { EcoAvatar, type EcoFlash, type EcoMood } from './EcoAvatar.js'
import { EcoAction, EcoChip } from './EcoButtons.js'
import { EcoReply, groupTurns } from './EcoReply.js'
import { ReportDraft } from './ReportDraft.js'
import { closeEco, openEco, setEcoSession, takeEcoPrompt, useEcoState } from './store.js'

/**
 * Eco's conversation, as a side panel beside whatever is open, or as its own
 * page (`/eco`) with the conversations down the side.
 *
 * It is the job tab's conversation (decision 94) widened: the same stream and
 * the same cards that wait for Approve, with pictures, and each answer drawn
 * as one piece (`EcoReply`) with its thumbs. What is on screen goes with every
 * message (`focus`) and is named above the box, where it can be left out.
 * The panel does not cover the page: the board stays live behind it while Eco
 * books. The same conversation carries from the panel to the page and back.
 * Decision 116, and its 2026-11-01 amendment.
 */

/** What the person is looking at, from the address: a job over the screen, a client, an agreement. */
function useFocus(): AssistantFocus {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const client = location.pathname.match(/^\/clients\/([^/]+)$/)?.[1]
  const agreement = location.pathname.match(/^\/agreements\/([^/]+)$/)?.[1]
  return {
    jobId: params.get('jobId'),
    clientId: client && client !== 'duplicates' ? client : null,
    agreementId: agreement ?? null,
    path: location.pathname,
  }
}

interface SpeechResultEvent {
  resultIndex: number
  results: ArrayLike<ArrayLike<{ transcript: string }> & { isFinal: boolean }>
}
interface SpeechRecogniser {
  lang: string
  interimResults: boolean
  continuous: boolean
  onresult: ((event: SpeechResultEvent) => void) | null
  onend: (() => void) | null
  onerror: (() => void) | null
  start(): void
  stop(): void
}

function speechRecogniser(): (new () => SpeechRecogniser) | null {
  const scope = window as unknown as { SpeechRecognition?: new () => SpeechRecogniser; webkitSpeechRecognition?: new () => SpeechRecogniser }
  return scope.SpeechRecognition ?? scope.webkitSpeechRecognition ?? null
}

/** Today, Yesterday or the date, for the conversations list. */
function dayOf(date: Date | string): string {
  const at = new Date(date)
  const today = new Date()
  const start = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate()).getTime()
  const days = Math.round((start(today) - start(at)) / 86_400_000)
  if (days === 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days < 7) return 'This week'
  return 'Earlier'
}

export function EcoPanel({ layout = 'drawer' }: { layout?: 'drawer' | 'page' }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { user } = useSession()
  const focus = useFocus()
  const page = layout === 'page'

  const list = useQuery(trpc.assistant.ecoSessions.queryOptions())
  const { sessionId } = useEcoState()
  // A conversation that ends on the person's message is one Eco is still
  // answering — the stream dropped (a phone asleep, a reload), but the server
  // carries on. Read it again until the answer lands. Through a ref, because
  // whether a stream is running is known only once the conversation below
  // has this query's answer.
  const streamRunning = useRef(false)
  const session = useQuery({
    ...trpc.assistant.session.queryOptions({ id: sessionId ?? '' }),
    enabled: sessionId !== null,
    refetchInterval: (query) =>
      !streamRunning.current && query.state.data?.turns.at(-1)?.role === 'USER' ? 2000 : false,
  })

  const [text, setText] = useState('')
  const [history, setHistory] = useState(false)
  const [flash, setFlash] = useState<EcoFlash>(null)
  const [listening, setListening] = useState(false)
  const [sms, setSms] = useState<PhoneText | null>(null)
  const [report, setReport] = useState<{ kind: 'bug' | 'feature'; summary: string; details: string } | null>(null)

  // What is on screen, named — and left out of the next messages if the
  // person says so, until they open something else.
  const focusKey = [focus.jobId, focus.clientId, focus.agreementId].filter(Boolean).join(':')
  const [ignored, setIgnored] = useState<string | null>(null)
  const named = useQuery({
    ...trpc.assistant.focus.queryOptions({ jobId: focus.jobId, clientId: focus.clientId, agreementId: focus.agreementId }),
    enabled: focusKey !== '',
  })
  const left = focusKey !== '' && ignored === focusKey
  const looking = focusKey !== '' && !left ? (named.data ?? null) : null
  // The chip only names it: what is open goes with the message unless the
  // person left it out, even before (or without) its name arriving.
  const sent: AssistantFocus = left ? { path: focus.path } : focus

  const live = list.data?.live ?? false
  const input = useRef<HTMLTextAreaElement>(null)
  const recogniser = useRef<SpeechRecogniser | null>(null)
  const chose = useRef(false)

  const start = useMutation(trpc.assistant.startEco.mutationOptions())

  // Eco can touch anything in the office, so anything may need reading again
  // — and the conversation too, in case the stream dropped before it ended.
  const refresh = useCallback(() => {
    for (const filter of [trpc.jobs, trpc.schedule, trpc.inbox, trpc.clients, trpc.agreements, trpc.tasks, trpc.desk]) {
      void queryClient.invalidateQueries(filter.pathFilter())
    }
    void queryClient.invalidateQueries(trpc.assistant.session.pathFilter())
    void queryClient.invalidateQueries(trpc.assistant.ecoSessions.pathFilter())
  }, [queryClient, trpc])

  const conversation = useConversation({
    sessionId,
    saved: session.data?.turns,
    refresh,
    silence: 'Eco did not answer.',
    // What is on screen goes with every message, so "this job" is the open one.
    prepare: (request) => ({ ...request, focus: sent }) as AssistantRunRequest,
    onSaved: () => void queryClient.invalidateQueries(trpc.assistant.ecoSessions.pathFilter()),
    onToolCall: (call) => {
      if (call.status === 'AWAITING_APPROVAL') setFlash('alert')
      if (call.tier === 'approve' && call.status === 'EXECUTED') setFlash('happy')
      if (call.status === 'FAILED' && call.tier !== 'read') setFlash('error')
    },
    onFailure: () => setFlash('error'),
    onPhoneText: setSms,
  })
  const { turns, setTurns, streaming, running, problem, setProblem, scroller, run, approve, reject, undo, undoing } = conversation
  streamRunning.current = running

  const newChat = async () => {
    const created = await start.mutateAsync()
    setEcoSession(created.id)
    setTurns([])
    setReport(null)
    setHistory(false)
    void queryClient.invalidateQueries(trpc.assistant.ecoSessions.pathFilter())
    return created.id
  }

  const send = async (words: string) => {
    const message = words.trim()
    if (!message || running || !live) return
    setText('')
    setReport(null)
    let id = sessionId
    if (!id) {
      try {
        id = await newChat()
      } catch (error) {
        setProblem(errorMessage(error))
        return
      }
    }
    void run({ sessionId: id, action: 'message', text: message })
  }

  // The latest conversation, once, the first time the list arrives — if it is
  // from the last twelve hours. Older ones are a tap away in the list.
  useEffect(() => {
    if (!list.data || chose.current) return
    chose.current = true
    if (sessionId) return
    const latest = list.data.sessions[0]
    if (latest && Date.now() - new Date(latest.updatedAt).getTime() < 12 * 3600_000) setEcoSession(latest.id)
  }, [list.data, sessionId])

  // "Ask Eco about this" from elsewhere in the app.
  useEffect(() => {
    if (!list.data) return
    const prompt = takeEcoPrompt()
    if (prompt) void send(prompt)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list.data])

  useEffect(() => input.current?.focus(), [])

  const listen = () => {
    const Recogniser = speechRecogniser()
    if (!Recogniser) return
    if (listening) {
      recogniser.current?.stop()
      return
    }
    const heard = new Recogniser()
    heard.lang = 'en-AU'
    heard.interimResults = true
    heard.continuous = false
    const before = text ? `${text.trimEnd()} ` : ''
    heard.onresult = (event) => {
      let words = ''
      for (let index = 0; index < event.results.length; index += 1) words += event.results[index]![0]!.transcript
      setText(before + words)
    }
    heard.onend = () => {
      setListening(false)
      input.current?.focus()
    }
    heard.onerror = () => setListening(false)
    recogniser.current = heard
    setListening(true)
    heard.start()
  }

  const asked = [...turns].reverse().find((turn) => turn.role === 'USER')?.content ?? null

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void send(text)
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void send(text)
    } else if (event.key === 'ArrowUp' && text === '' && asked) {
      // ↑ in an empty box brings back the last message, to fix and send again.
      event.preventDefault()
      setText(asked)
    }
  }

  const goToWaiting = () => {
    const card = scroller.current?.querySelector('[data-waiting]')
    card?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }

  const waiting = turns.reduce((count, turn) => count + turn.toolCalls.filter((call) => call.status === 'AWAITING_APPROVAL').length, 0)
  const catchingUp = !running && turns.at(-1)?.role === 'USER'
  const working = running || catchingUp
  // Said at once on Stop, until the server's own "Stopped" turn arrives.
  const stopping = conversation.stopped && !running && turns.at(-1)?.content !== ASSISTANT_STOPPED
  const mood: EcoMood = listening ? 'listening' : working ? (streaming ? 'talking' : 'thinking') : 'idle'
  const status = !list.data
    ? 'Getting ready…'
    : !live
      ? 'Not connected'
      : listening
        ? 'Listening…'
        : working
          ? streaming
            ? 'Writing…'
            : 'Thinking…'
          : 'Ready'
  const firstName = user?.name.split(/\s+/)[0] ?? 'there'
  const empty = turns.length === 0 && !streaming && !running
  const canSpeak = speechRecogniser() !== null
  const entries = groupTurns(turns)
  const lastEco = entries.at(-1)?.kind === 'eco' ? entries.at(-1) : null
  const suggestions =
    looking?.kind === 'job' ? ECO_JOB_SUGGESTIONS : looking?.kind === 'client' ? ECO_CLIENT_SUGGESTIONS : ECO_SUGGESTIONS

  const conversations = (
    <ConversationList
      sessions={list.data?.sessions ?? []}
      current={sessionId}
      onPick={(id) => {
        setEcoSession(id)
        setReport(null)
        setHistory(false)
      }}
    />
  )

  return (
    <aside
      aria-label="Eco"
      className={cn(
        'flex flex-col bg-ink-50',
        page
          ? 'h-full min-h-0'
          : // Above a job card (z-50), because "ask about this job" is when Eco is opened over one.
            'fixed inset-0 z-[55] shadow-[-12px_0_32px_-8px_rgb(16_24_40/0.25)] sm:inset-y-0 sm:right-0 sm:left-auto sm:w-[460px] sm:border-l sm:border-ink-200',
      )}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && !page) closeEco()
      }}
    >
      <header className="flex h-16 shrink-0 items-center gap-3 bg-ink-900 pt-[var(--safe-top,0px)] pr-2 pl-2.5 text-white">
        <EcoAvatar mood={mood} flash={flash} onFlashed={() => setFlash(null)} className="h-[48px] w-[52px]" />
        <div className="min-w-0 flex-1">
          <h2 className="font-display text-[19px] leading-tight font-extrabold">Eco</h2>
          <p className="flex items-center gap-1.5 text-[11.5px] text-brand-300" role="status" aria-live="polite">
            <span className={cn('size-1.5 rounded-[2px]', live ? 'bg-brand-500' : 'bg-ink-500')} aria-hidden="true" />
            {status}
            {waiting && !working ? (
              <button type="button" onClick={goToWaiting} className="ml-1 font-semibold text-white underline-offset-2 hover:underline">
                · {waiting} waiting for you ↓
              </button>
            ) : null}
          </p>
        </div>
        {/* The page lists them down the side on a desk; a phone has no room for that. */}
        <HeaderButton
          label="Conversations"
          icon="timeline"
          pressed={history}
          onClick={() => setHistory((open) => !open)}
          className={page ? 'lg:hidden' : undefined}
        />
        <HeaderButton
          label="New conversation"
          icon="plus"
          disabled={!live || running || start.isPending}
          onClick={() => void newChat().catch((error: unknown) => setProblem(errorMessage(error)))}
        />
        {page ? (
          <HeaderButton
            label="Open as a side panel"
            icon="collapse"
            onClick={() => {
              openEco()
              if (window.history.length > 1) navigate(-1)
              else navigate('/')
            }}
          />
        ) : (
          <>
            <HeaderButton
              label="Open full page"
              icon="expand"
              onClick={() => {
                closeEco()
                navigate('/eco')
              }}
            />
            <HeaderButton label="Close Eco" icon="close" onClick={closeEco} />
          </>
        )}
      </header>

      <div className="flex min-h-0 flex-1">
        {page ? (
          <nav aria-label="Conversations" className="hidden w-64 shrink-0 overflow-y-auto border-r border-ink-200 bg-white lg:block">
            {conversations}
          </nav>
        ) : null}

        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
          {history ? (
            <nav aria-label="Conversations" className={cn('max-h-[45%] shrink-0 overflow-y-auto border-b border-ink-200 bg-white', page && 'lg:hidden')}>
              {conversations}
            </nav>
          ) : null}

          <div
            ref={scroller}
            onScroll={conversation.onScroll}
            role="log"
            aria-label="Conversation with Eco"
            className="min-h-0 flex-1 overflow-y-auto px-4 py-4"
          >
            <div className={cn('flex flex-col gap-4', page && 'mx-auto max-w-3xl')}>
              {list.data && !live ? (
                <div className="rounded-lg border border-ink-200 bg-white px-4 py-3 text-[13px] text-ink-700">
                  <p className="font-semibold text-ink-900">Eco is not connected here.</p>
                  <p>It needs a model key on the server (OpenAI or Claude). Everything else works as usual.</p>
                </div>
              ) : null}

              {empty && live ? (
                <div className="flex flex-col items-center gap-2 pt-6 pb-2 text-center">
                  <EcoAvatar mood="idle" className="h-[104px] w-[112px]" />
                  <p className="font-display text-[20px] font-extrabold text-ink-900">G’day {firstName}.</p>
                  <p className="max-w-[22rem] text-[13px] text-ink-600">
                    {looking
                      ? `Ask me about ${looking.label}, or anything else. You approve anything that matters first.`
                      : 'Ask me about today, a customer, an agreement or the board. I can book, write emails and texts, and change things — you approve anything that matters first.'}
                  </p>
                </div>
              ) : null}

              {entries.map((entry, index) =>
                entry.kind === 'user' ? (
                  <div key={entry.turn.id} className="flex justify-end">
                    <p
                      className="max-w-[85%] rounded-lg bg-ink-900 px-3.5 py-2 text-[13.5px] whitespace-pre-wrap text-white"
                      title={new Intl.DateTimeFormat('en-AU', { weekday: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(entry.turn.occurredAt))}
                    >
                      {entry.turn.content}
                    </p>
                  </div>
                ) : (
                  <EcoReply
                    key={entry.id}
                    turns={entry.turns}
                    live={working && entry === lastEco && index === entries.length - 1}
                    streaming={entry === lastEco ? streaming : undefined}
                    busy={running}
                    asked={asked}
                    onApprove={approve}
                    onReject={reject}
                    onUndo={undo}
                    undoing={undoing}
                    onPick={(prompt) => void send(prompt)}
                    onRetry={(why) => void send(`That wasn’t right (${why.toLowerCase()}). Please try again.`)}
                    onReport={setReport}
                  />
                ),
              )}

              {streaming && !lastEco ? (
                <EcoReply
                  turns={[]}
                  live
                  streaming={streaming}
                  busy
                  asked={asked}
                  onApprove={approve}
                  onReject={reject}
                  onUndo={undo}
                  undoing={undoing}
                  onPick={() => {}}
                  onRetry={() => {}}
                  onReport={setReport}
                />
              ) : null}

              {working && !streaming ? (
                <p className="flex items-center gap-2 pl-1 text-[12.5px] text-ink-500" role="status">
                  <Spinner tone="brand" className="size-4" /> {stopping ? 'Stopping after this step…' : 'Eco is working on it…'}
                </p>
              ) : stopping ? (
                <p className="flex items-center gap-2 pl-1 text-[12.5px] text-ink-500" role="status">
                  <Icon name="check" className="size-4 text-ink-400" /> Stopped. Anything already done stays done.
                </p>
              ) : null}

              {problem ? (
                <div role="alert" className="flex flex-col gap-2 rounded-lg bg-red-50 px-3 py-2.5 text-[13px] text-red-800">
                  <p>{problem}</p>
                  <div className="flex flex-wrap gap-3 text-[12.5px] font-semibold">
                    {asked ? (
                      <button type="button" disabled={running} className="text-red-800 underline-offset-2 hover:underline" onClick={() => void send(asked)}>
                        Try again
                      </button>
                    ) : null}
                    <button
                      type="button"
                      className="text-red-800 underline-offset-2 hover:underline"
                      onClick={() =>
                        setReport({
                          kind: 'bug',
                          summary: `Eco failed: ${problem}`.slice(0, 200),
                          details: `I asked Eco: “${asked ?? ''}”\nIt said: “${problem}”`,
                        })
                      }
                    >
                      Report it
                    </button>
                  </div>
                </div>
              ) : null}

              {report ? (
                <ReportDraft
                  key={`${report.kind}-${report.summary}`}
                  {...report}
                  sessionId={sessionId}
                  path={focus.path ?? '/eco'}
                  onClose={() => setReport(null)}
                />
              ) : null}
            </div>
          </div>

          {!conversation.atBottom ? (
            <button
              type="button"
              onClick={conversation.jumpToLatest}
              className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-md bg-ink-900 px-3 py-1.5 text-[12px] font-semibold text-white shadow-md"
            >
              <Icon name="chevronDown" className="size-3.5" />
              Jump to the latest
            </button>
          ) : null}
        </div>
      </div>

      <div className={cn('shrink-0 border-t border-ink-200 bg-white px-3 pt-2 pb-[calc(0.75rem+var(--safe-bottom,0px))]')}>
        <div className={cn(page && 'mx-auto max-w-3xl')}>
          {looking ? (
            <div className="mb-2 flex items-center gap-1.5 text-[12px] text-ink-600">
              <span className="inline-flex min-w-0 items-center gap-1.5 rounded-md bg-ink-50 py-0.5 pr-1 pl-2 ring-1 ring-ink-200 ring-inset">
                <Icon name="eye" className="size-3.5 shrink-0 text-ink-400" />
                <span className="truncate">
                  Looking at <span className="font-semibold text-ink-900">{looking.label}</span>
                </span>
                <button
                  type="button"
                  aria-label="Leave it out"
                  title="Leave it out of the next messages"
                  onClick={() => setIgnored(focusKey)}
                  className="grid size-5 shrink-0 place-items-center rounded-sm text-ink-400 hover:bg-ink-200 hover:text-ink-900"
                >
                  <Icon name="close" className="size-3" />
                </button>
              </span>
            </div>
          ) : null}
          {empty && live ? (
            <div className="mb-2 flex flex-wrap gap-1.5" role="group" aria-label="Suggestions">
              {suggestions.map((suggestion) => (
                <EcoChip key={suggestion.label} disabled={running} onClick={() => void send(suggestion.prompt)}>
                  {suggestion.label}
                </EcoChip>
              ))}
            </div>
          ) : null}
          <form onSubmit={submit} className="ecb-input">
            <textarea
              ref={input}
              aria-label="Message Eco"
              rows={1}
              value={text}
              disabled={!live}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={onKeyDown}
              placeholder={looking?.kind === 'job' ? 'Ask about this job, or anything else…' : 'Ask Eco, or tell it what to do…'}
              className="max-h-40 min-h-[40px] flex-1 resize-none bg-transparent py-2.5 text-[13.5px] text-ink-900 outline-none field-sizing-content placeholder:text-ink-400"
            />
            {canSpeak ? (
              <EcoAction
                badge="voice"
                aria-label={listening ? 'Stop listening' : 'Speak to Eco'}
                title="Listen"
                aria-pressed={listening}
                disabled={!live || running}
                onClick={listen}
              />
            ) : null}
            {running ? (
              <EcoAction
                badge="stop"
                aria-label="Stop"
                title="Stop"
                onClick={() => {
                  conversation.stop()
                  // The server says "Stopped" in a moment; fetch it.
                  window.setTimeout(() => void session.refetch(), 1500)
                }}
              />
            ) : (
              <EcoAction badge="send" type="submit" aria-label="Send" title="Send" disabled={!live || !text.trim()} />
            )}
          </form>
          <p className="mt-1.5 flex flex-wrap justify-between gap-x-3 px-1 text-[10.5px] text-ink-400">
            <span>Eco can be wrong. Bookings, money and anything to a customer wait for your Approve.</span>
            <span className="hidden sm:inline">Enter sends · Shift+Enter new line · ↑ last message</span>
          </p>
        </div>
      </div>

      <SmsCompose
        open={sms !== null}
        onClose={() => setSms(null)}
        contactName=""
        phone={sms?.to ?? null}
        initialBody={sms?.body}
        jobId={sms?.jobId ?? null}
      />
    </aside>
  )
}

function ConversationList({
  sessions,
  current,
  onPick,
}: {
  sessions: Array<{ id: string; updatedAt: Date | string; opening: string | null }>
  current: string | null
  onPick: (id: string) => void
}) {
  if (!sessions.length) return <p className="px-4 py-3 text-[12.5px] text-ink-500">No conversations yet.</p>
  const groups = new Map<string, typeof sessions>()
  for (const row of sessions) groups.set(dayOf(row.updatedAt), [...(groups.get(dayOf(row.updatedAt)) ?? []), row])
  return (
    <div className="flex flex-col py-1">
      {[...groups.entries()].map(([label, rows]) => (
        <section key={label} aria-label={label}>
          <p className="px-4 pt-2.5 pb-1 text-[10.5px] font-bold tracking-wider text-ink-400 uppercase">{label}</p>
          <ul>
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  aria-current={row.id === current ? 'true' : undefined}
                  onClick={() => onPick(row.id)}
                  className={cn(
                    'flex w-full flex-col px-4 py-1.5 text-left hover:bg-ink-50',
                    row.id === current && 'bg-brand-50 shadow-[inset_3px_0_0_var(--color-brand-500)]',
                  )}
                >
                  <span className="truncate text-[12.5px] font-medium text-ink-900">{row.opening ?? 'New conversation'}</span>
                  <span className="text-[11px] text-ink-500">
                    {new Intl.DateTimeFormat('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(new Date(row.updatedAt))}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  )
}

function HeaderButton({
  label,
  icon,
  onClick,
  disabled,
  pressed,
  className,
}: {
  label: string
  icon: IconName
  onClick: () => void
  disabled?: boolean
  pressed?: boolean
  className?: string | undefined
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-md text-ink-300 hover:bg-white/10 hover:text-white disabled:opacity-40',
        pressed && 'bg-white/10 text-white',
        className,
      )}
    >
      <Icon name={icon} className="size-[18px]" />
    </button>
  )
}
